import React from "react";
import { Printer } from "lucide-react";
import { toast } from "react-toastify";
import api from "../services/api";
const qz = require('qz-tray');

interface Currency {
  id: number;
  currency_name: string;
  currency_symbol: string;
  currency_code: string;
  exchange_rate: number;
}

interface Company {
  id: number;
  company_name: string;
  currency?: Currency;
  [key: string]: any;
}

interface BookingItem {
  id: number;
  product_id: number;
  quantity: number;
  unit_price: number;
  amount: number;
  [key: string]: any;
}

interface Location {
  id: number;
  name: string;
  [key: string]: any;
}

interface Booking {
  id: number;
  user_id: string;
  location_id: string;
  status: string;
  payment_status: string;
  payment_gateway: string;
  product_amount: number;
  tax_amount: number;
  amount_to_pay: number;
  formated_amount_to_pay: string;
  company?: Company;
  items?: any;
  location?: Location;
  [key: string]: any;
}

interface PaymentApiResponse {
  payment_status: string;
  booking: Booking;
  message: string;
}

interface PaymentCompletedModalProps {
  isOpen: boolean;
  onClose: () => void;
  apiResponse?: PaymentApiResponse;
  totalTendered: number;
  changeDue: number;
  bookingId?: number | string | null;
  onPaymentComplete?: (bookingId: number | string) => void;
  isQuickPayment?: boolean;
  isOfflineQueued?: boolean;
  quickPaymentData?: {
    amount: number;
    paymentType: string;
    stylistName?: string;
  };
}

const PaymentCompletedModal: React.FC<PaymentCompletedModalProps> = ({
  isOpen,
  onClose,
  apiResponse,
  totalTendered,
  changeDue,
  bookingId,
  onPaymentComplete,
  isQuickPayment,
  isOfflineQueued = false,
  quickPaymentData,
}) => {
  // Get user from local storage for printer settings
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const companySettings = user?.company || {};

  // Get currency symbol from booking company or user company
  const currencySymbol = apiResponse?.booking?.company?.currency?.currency_symbol
    || user?.company?.currency_symbol
    || user?.company?.currency?.currency_symbol
    || '';

  const isDirectPrintEnabled = companySettings?.print_receipt === "active";

  // Log API response for debugging
  React.useEffect(() => {
    if (apiResponse && isOpen) {
      console.log("PaymentCompletedModal - Full API Response:", apiResponse);
      console.log("PaymentCompletedModal - Booking Details:", apiResponse.booking);
    }
  }, [apiResponse, isOpen]);

  // Auto-open cash drawer if enabled for this company
  React.useEffect(() => {
    if (!isOpen) return;
    const cashDrawerVal = companySettings?.is_cash_drawer_open;
    const isEnabled =
      cashDrawerVal === true ||
      cashDrawerVal === 1 ||
      cashDrawerVal === "1" ||
      cashDrawerVal === "active";
    if (isEnabled) {
      const timer = setTimeout(() => {
        window.location.href = "cashdrawer://open";
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (isOpen) {
      qz.security.setCertificatePromise(function (resolve: any, reject: any) {
        fetch("/qztray/digital-certificate.txt", { cache: 'no-store', headers: { 'Content-Type': 'text/plain' } })
          .then(function (data) {
            if (data.ok) return data.text();
            throw new Error('Certificate not found');
          })
          .then(function (text) { resolve(text); })
          .catch(function (err) { reject(err); });
      });

      qz.security.setSignatureAlgorithm("SHA256");
      qz.security.setSignaturePromise(function (toSign: any) {
        return function (resolve: any, reject: any) {
          // Send raw text to avoid any JSON encoding/escaping issues
          api.post("/admin/sign-message", toSign, {
            headers: { 'Content-Type': 'text/plain' }
          })
            .then((response: any) => {
              // Backend should return just the base64 signature string as the response body or in signedMessage
              const signature = typeof response.data === 'string' ? response.data : response.data.signedMessage;
              resolve(signature);
            })
            .catch((err: any) => {
              console.error("Signature error:", err);
              reject(err);
            });
        };
      });

      // Auto-print when direct print is enabled — triggers after QZ tray is set up
      if (isDirectPrintEnabled && !isOfflineQueued) {
        // Small delay to allow QZ tray security setup to complete
        setTimeout(() => {
          printReceipt();
        }, 800);
      }
    }
  }, [isOpen]);

  const handleDone = () => {
    // Call onPaymentComplete callback if available
    if (onPaymentComplete) {
      console.log("Calling onPaymentComplete with bookingId:", bookingId);
      onPaymentComplete(bookingId || "unknown");
    }

    // Close modal
    onClose();
  };

  if (!isOpen) return null;

  const printReceipt = async () => {
    const printerName = companySettings?.printer_name;

    if (!printerName) {
      // You might want to show a UI error (toast) here if possible, or just log
      console.error("No printer configured for this company.");
      toast.error("Printer is not configured.");
      return;
    }

    try {
      if (!qz.websocket.isActive()) {
        try {
          await qz.websocket.connect();
          console.log("QZ TRAY CONNECTED!");
        } catch (err: any) {
          if (err.message && err.message.includes("An open connection with QZ Tray already exists")) {
            console.warn("Connection already active, proceeding...");
          } else {
            console.error("Failed to connect to QZ Tray:", err);
            return;
          }
        }
      }

      // Determine width based on format (default 80mm/48 chars)
      const is58mm = companySettings?.receipt_format === '58mm';
      // For 58mm paper, use 32 characters per line (matches typical payout layout)
      const receiptWidth = is58mm ? 32 : 48;

      // Helper for spacing (used for item/total lines)
      const formatLine = (left: string, right: string, width: number = receiptWidth) => {
        const leftLimit = width - right.length - 1;
        let leftText = left;
        if (leftText.length > leftLimit) {
          leftText = leftText.substring(0, leftLimit) + "..";
        }
        const space = Math.max(0, width - leftText.length - right.length);
        return leftText + " ".repeat(space) + right + "\n";
      };

      // Ensure header lines (company name, address, etc.) never exceed the
      // configured receipt width (important for 58mm so they don't look 80mm-wide)
      const headerLine = (text: string) => {
        if (!text) return "\n";
        const trimmed = text.length > receiptWidth ? text.substring(0, receiptWidth) : text;
        return trimmed + "\n";
      };

      const separator = '-'.repeat(receiptWidth) + '\x0A';

      const booking = apiResponse?.booking;
      const company = booking?.company || companySettings;
      const items = booking?.items || [];
      const usedCurrencySymbol = currencySymbol || '';

      let receiptItems = '';
      if (isQuickPayment && quickPaymentData) {
        receiptItems += formatLine('Quick Payment', `${usedCurrencySymbol}${quickPaymentData.amount.toFixed(2)}`);
        if (quickPaymentData.stylistName) {
          receiptItems += formatLine('Stylist', quickPaymentData.stylistName);
        }
      } else {
        items.forEach((item: any) => {
          let name = 'Item';
          if (item.product) {
            name = item.product.name;
          } else if (item.business_service) {
            name = item.business_service.name;
          } else if (item.sunbed) {
            name = item.sunbed.label;
            if (item.sunbed_mins) {
              name += ` (${item.sunbed_mins} mins)`;
            }
          }

          const price = `${usedCurrencySymbol}${parseFloat(item.amount).toFixed(2)}`;
          const qty = item.quantity;
          const line = `${name} (x${qty})`;
          receiptItems += formatLine(line, price);
        });
      }

      const data: string[] = [
        '\x1B\x40',  // Initialize printer (ESC @)
        '\u001Bt\u0010',
        // Title in Center
        '\x1B\x61\x01', // Center align (ESC a 1)
        headerLine(company?.company_name || 'Glossa'),
        headerLine(company?.address || ''),
        // `${company?.company_phone || ''}\n`,
        separator,
        // Date, Time, Customer Type (left aligned)
        '\x1B\x61\x00', // Left align (ESC a 0)
        `Date: ${new Date(booking?.date_time || Date.now()).toLocaleDateString()}\n`,
        `Time: ${new Date(booking?.date_time || Date.now()).toLocaleTimeString("en-US", { hour12: true })}\n`,
        !isQuickPayment ? `Booking ID: #${booking?.id}\n` : '',
        separator,

        // Items
        receiptItems,
        separator,

        // Totals
        ...(isQuickPayment && quickPaymentData ? [
          formatLine('Total', `${usedCurrencySymbol}${quickPaymentData.amount.toFixed(2)}`),
          separator,
          `Payment: ${quickPaymentData.paymentType.charAt(0).toUpperCase() + quickPaymentData.paymentType.slice(1)}\n`,
        ] : [
          formatLine('Sub total', `${usedCurrencySymbol}${(booking?.original_amount || 0).toFixed(2)}`),
          formatLine('Taxes', `${usedCurrencySymbol}${(booking?.tax_amount || 0).toFixed(2)}`),
          separator,
          formatLine('Total', `${usedCurrencySymbol}${(booking?.amount_to_pay || 0).toFixed(2)}`),
          separator,
          `Payment: ${booking?.payment_gateway || 'Completed'}\n`,
        ]),
        '\x0A',

        // Thank You Message (Center aligned again)
        '\x1B\x61\x01', // Center align (ESC a 1)
        'Thank you for your visit!\n',
        '\x0A\x0A',

        // Full Cut
        '\x1D\x56\x42\x00' // Full cut (ESC/POS cut command)
      ];


      const config = qz.configs.create(printerName);

      // Send the print command to QZ Tray
      await qz.print(config, data);
      console.log("Printed Successfully");

    } catch (err: any) {
      console.error("Print Error: ", err);
      toast.error("Error printing receipt: " + (err.message || "Unknown error"));
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1200] animate-fadeIn"
      style={{ display: 'flex', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1200 }}
      data-testid="success-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-[90%] mx-4 transform transition-all duration-300 animate-scaleIn relative border border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Print Icon */}
        {isDirectPrintEnabled && !isOfflineQueued && (
          <button
            onClick={() => printReceipt()}
            className="absolute top-4 right-4 text-gray-600 hover:text-[#885ABB] transition-colors"
            title="Print Receipt"
          >
            <Printer className="w-7 h-7" />
          </button>
        )}

        {/* Title */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {isOfflineQueued ? "Sale Saved Offline" : "Payment Completed"}
          </h2>
        </div>

        {/* Payment Summary */}
        <div className="flex justify-between items-center mb-8">
          {/* Total Tendered */}
          <div className="flex flex-col items-center flex-1">
            <span className="text-sm text-gray-600 mb-2">Total Tendered</span>
            <span className="text-3xl font-bold text-gray-800">
              {currencySymbol}{totalTendered.toFixed(2)}
            </span>
          </div>

          {/* Change Due */}
          <div className="flex flex-col items-center flex-1">
            <span className="text-sm text-[#885ABB] mb-2">Change Due</span>
            <span className="text-3xl font-bold text-[#885ABB]">
              {currencySymbol}{changeDue.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Done Button */}
        <button
          onClick={handleDone}
          className="w-full py-4 bg-[#885ABB] text-white rounded-lg font-semibold text-lg hover:bg-[#6f3fa8] transition-colors shadow-md"
        >
          Done
        </button>
      </div>
    </div>
  );
};

export default PaymentCompletedModal;
